/**
 * The ONE "batch created" toast of the Order Queue's three create paths
 * (manual selection, scoped preview, scoped create-now), plus what the batch's
 * just-in-time sync reported (`createBatch` → `justInTime`):
 *
 *   undefined  no word on it (the lost-response path, which only learns the
 *              batch number): the plain toast. The pick list is the truth.
 *   null       the sync failed (the batch stands). If the batch has blanks
 *              still waiting for a buy list (ONE getBuyList read, only on this
 *              path), a warning that stays: open the batch, Update buy list.
 *   {POs: []}  no just-in-time blank: the plain toast.
 *   {POs: n}   the plain title plus "{n} just-in-time POs for {m} blanks" and
 *              "Open batch", which lands on the batch's Buy first card.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CreateBatchJustInTimeSummary } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { BUY_FIRST_ANCHOR } from '../utils/buy-first';

export type JustInTimeNotice =
  | { kind: 'none' }
  | { kind: 'purchaseOrders'; purchaseOrders: number; blanks: number }
  | { kind: 'checkBuyList' };

/** What the toast should say about the batch's just-in-time POs (pure). */
export function planJustInTimeNotice(justInTime: CreateBatchJustInTimeSummary | null | undefined): JustInTimeNotice {
  if (justInTime === undefined) return { kind: 'none' };
  if (justInTime === null) return { kind: 'checkBuyList' };
  if (justInTime.purchaseOrders.length === 0) return { kind: 'none' };
  return { kind: 'purchaseOrders', purchaseOrders: justInTime.purchaseOrders.length, blanks: justInTime.units };
}

export interface BatchCreatedInfo {
  batchNumber: number;
  orderCount: number;
  /** The createBatch response's batch id and just-in-time summary; absent on the lost-response path. */
  batchId?: string;
  justInTime?: CreateBatchJustInTimeSummary | null;
}

export function useBatchCreatedToast() {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  return useCallback(
    ({ batchNumber, orderCount, batchId, justInTime }: BatchCreatedInfo) => {
      const title = t('orders.batchCreated', { number: batchNumber, count: orderCount });
      const notice = planJustInTimeNotice(justInTime);
      const openBatch = batchId
        ? {
            label: t('batch.buyFirst.toastOpenBatch'),
            onClick: () => navigate(`/production/batches/${batchId}#${BUY_FIRST_ANCHOR}`),
          }
        : undefined;

      if (notice.kind === 'purchaseOrders') {
        toast.success(title, {
          description: t('batch.buyFirst.toastPurchaseOrders', {
            count: notice.purchaseOrders,
            blanks: t('batch.buyFirst.blankCount', { count: notice.blanks }),
          }),
          action: openBatch,
          duration: 10_000,
        });
        return;
      }

      toast.success(title);
      if (notice.kind !== 'checkBuyList' || !batchId) return;

      // The sync failed after the batch committed. Only a batch that has
      // blanks waiting for a buy list needs telling; a failed read says
      // nothing more (the pick list shows the same notice when opened).
      void utils.productionOrders.getBuyList
        .fetch({ batchId })
        .then((buyList) => {
          if (buyList.unlinkedUnits > 0) {
            toast.warning(t('batch.buyFirst.toastNotMade'), { action: openBatch, duration: Infinity });
          }
        })
        .catch(() => undefined);
    },
    [t, navigate, utils]
  );
}
